/**
 * Version information service.
 *
 * Reads app version and changelog data from meta tags injected by Flask.
 */

export interface ChangelogEntry {
    version: string;
    date: string | null;
    sections: Record<string, string[]>;
    isUnreleased: boolean;
}

export interface VersionInfo {
    version: string;
    isDevelopment: boolean;
    hasUnreleasedChanges: boolean;
    unreleasedEntry: ChangelogEntry | null;
    recentEntries: ChangelogEntry[];
}

let cachedVersionInfo: VersionInfo | null = null;

/**
 * Get the application version string.
 */
export function getAppVersion(): string {
    const meta = document.head.querySelector('meta[name=bf-app-version]') as HTMLMetaElement;
    return meta?.content || '0.0.0';
}

/**
 * Check if this is a development build (has unreleased changes).
 */
export function isDevelopmentBuild(): boolean {
    const meta = document.head.querySelector('meta[name=bf-app-is-dev]') as HTMLMetaElement;
    return meta?.content === 'true';
}

/**
 * Get complete version info including changelog entries.
 */
export function getVersionInfo(): VersionInfo {
    if (cachedVersionInfo) {
        return cachedVersionInfo;
    }

    const meta = document.head.querySelector('meta[name=bf-app-changelog]') as HTMLMetaElement;
    if (!meta?.content) {
        cachedVersionInfo = {
            version: getAppVersion(),
            isDevelopment: isDevelopmentBuild(),
            hasUnreleasedChanges: false,
            unreleasedEntry: null,
            recentEntries: [],
        };
        return cachedVersionInfo;
    }

    try {
        cachedVersionInfo = JSON.parse(meta.content) as VersionInfo;
        return cachedVersionInfo;
    } catch {
        cachedVersionInfo = {
            version: getAppVersion(),
            isDevelopment: isDevelopmentBuild(),
            hasUnreleasedChanges: false,
            unreleasedEntry: null,
            recentEntries: [],
        };
        return cachedVersionInfo;
    }
}

/**
 * Get display version string (includes -dev suffix if development build).
 */
export function getDisplayVersion(): string {
    const info = getVersionInfo();
    return info.isDevelopment ? `${info.version}-dev` : info.version;
}
